import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { graphql } from 'gatsby';

import classNames from 'classnames/bind';

import SEO from 'components/shared/seo/seo';
import Header from 'components/shared/header';
import Footer from 'components/shared/footer';
import MobileMenu from 'components/shared/mobile-menu';
import Banner from 'components/shared/banner';
import CookieBanner from 'components/shared/cookie-banner';
import PopupBanner from 'components/shared/popup-banner';
import PopupBannerProductDemo from 'components/shared/popup-banner-product-demo';

import useParamDetectionUrl from 'hooks/use-param-detection-url';

import MainContext from 'context/main';

import styles from './layout-main.module.scss';
import GlobalPopupBanner from '../../components/shared/GlobalPopupBanner';

const cx = classNames.bind(styles);

const LayoutMain = (props) => {
  const {
    seo,
    headerLayout,
    headerTheme,
    headerWithGradient,
    footerTheme,
    withBanner,
    withBottomOffset,
    pageContext,
    location,
    children,
  } = props;

  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const handleHeaderBurgerClick = () => setIsMobileMenuOpen(!isMobileMenuOpen);

  // detecting advertising parameters
  useParamDetectionUrl();

  // useEffect(() => {
  //   if (isMobileMenuOpen) {
  //     // documentElement = html
  //     document.documentElement.style.cssText = 'overflow: hidden';
  //     document.body.style.cssText = 'overflow: hidden';
  //   } else {
  //     // documentElement = html
  //     document.documentElement.style.cssText = '';
  //     document.body.style.cssText = '';
  //   }
  // }, [isMobileMenuOpen]);

  const [isCookieBannerVisible, setIsCookieBannerVisible] = useState(false);
  const [isPopupBannerVisible, setIsPopupBannerVisible] = useState(false);

  const [showCookieBanner, setShowCookieBanner] = useState(false);

  useEffect(() => {
    // Magic number to postpone rendering and Cookie Banner logic execution
    const timeout = setTimeout(() => {
      setShowCookieBanner(true);
    }, 8000);
    return () => {
      clearTimeout(timeout);
    };
  }, []);

  return (
    <MainContext.Provider value={pageContext}>
      {seo && <SEO {...seo} />}
      <div style={{ position: 'sticky', top: 0, zIndex: 10 }}>
        {withBanner && <Banner />}
        <Header
          layout={headerLayout}
          theme={headerTheme}
          withGradient={headerWithGradient}
          isBurgerToggled={isMobileMenuOpen}
          onBurgerClick={handleHeaderBurgerClick}
        />
      </div>

      <main
        className={cx('content', { withBottomOffset })}
        style={{ overflowY: isMobileMenuOpen ? 'hidden' : undefined }}
      >
        {children}
      </main>

      <Footer theme={footerTheme} />
      {showCookieBanner && <CookieBanner setIsCookieBannerVisible={setIsCookieBannerVisible} />}
      {location && (
        <PopupBanner
          location={location}
          isCookieBannerVisible={isCookieBannerVisible}
          setIsPopupBannerVisible={setIsPopupBannerVisible}
        />
      )}
      {!isPopupBannerVisible && (
        <PopupBannerProductDemo location={location} isCookieBannerVisible={isCookieBannerVisible} />
      )}
      <MobileMenu withBanner={withBanner} isOpen={isMobileMenuOpen} />
      {pageContext.showPromptBanner && <GlobalPopupBanner />}
    </MainContext.Provider>
  );
};

LayoutMain.defaultProps = {
  seo: null,
  headerLayout: 'default',
  headerTheme: 'primary',
  headerWithGradient: true,
  footerTheme: null,
  withBanner: true,
  withBottomOffset: false,
  location: null,
  children: null,
};

LayoutMain.propTypes = {
  seo: PropTypes.objectOf(PropTypes.any),
  headerLayout: PropTypes.oneOf(['default', 'only-logo']),
  headerTheme: PropTypes.oneOf(['primary', 'secondary']),
  headerWithGradient: PropTypes.bool,
  footerTheme: PropTypes.oneOf(['default', 'with-border']),
  withBanner: PropTypes.bool,
  withBottomOffset: PropTypes.bool,
  pageContext: PropTypes.objectOf(PropTypes.any).isRequired,
  location: PropTypes.objectOf(PropTypes.any),
  children: PropTypes.node,
};

export const query = graphql`
  fragment wpPageSeo on WpPage {
    seo {
      title
      metaDesc
      metaKeywords
      metaRobotsNoindex
      opengraphDescription
      opengraphTitle
      opengraphUrl
      opengraphImage {
        localFile {
          childImageSharp {
            gatsbyImageData(formats: [JPG], width: 1200, height: 630)
          }
        }
      }
      canonical
      twitterTitle
      twitterDescription
      twitterImage {
        localFile {
          childImageSharp {
            gatsbyImageData(formats: [JPG], width: 1024, height: 512)
          }
        }
      }
    }
  }
  fragment wpPostSeo on WpPost {
    seo {
      title
      metaDesc
      metaKeywords
      opengraphDescription
      opengraphTitle
      opengraphUrl
      opengraphImage {
        localFile {
          childImageSharp {
            gatsbyImageData(formats: [JPG], width: 1200, height: 630)
          }
        }
      }
      canonical
      twitterTitle
      twitterDescription
      twitterImage {
        localFile {
          childImageSharp {
            gatsbyImageData(formats: [JPG], width: 1024, height: 512)
          }
        }
      }
    }
  }

  fragment wpPdfResourceSeo on WpPdfResource {
    seo {
      title
      metaDesc
      metaKeywords
      opengraphDescription
      opengraphTitle
      opengraphUrl
      opengraphImage {
        localFile {
          childImageSharp {
            gatsbyImageData(formats: [JPG], width: 1200, height: 630)
          }
        }
      }
      canonical
      twitterTitle
      twitterDescription
      twitterImage {
        localFile {
          childImageSharp {
            gatsbyImageData(formats: [JPG], width: 1024, height: 512)
          }
        }
      }
    }
  }

  fragment wpIntegrationSeo on WpIntegration {
    seo {
      title
      metaDesc
      metaKeywords
      opengraphDescription
      opengraphTitle
      opengraphUrl
      opengraphImage {
        localFile {
          childImageSharp {
            fixed(toFormat: JPG, width: 1200, height: 630) {
              src
            }
          }
        }
      }
      canonical
      twitterTitle
      twitterDescription
      twitterImage {
        localFile {
          childImageSharp {
            fixed(toFormat: JPG, width: 1024, height: 512) {
              src
            }
          }
        }
      }
    }
  }

  fragment wpWhitePaperSeo on WpWhitePaper {
    seo {
      title
      metaDesc
      metaKeywords
      opengraphDescription
      opengraphTitle
      opengraphUrl
      opengraphImage {
        localFile {
          childImageSharp {
            fixed(toFormat: JPG, width: 1200, height: 630) {
              src
            }
          }
        }
      }
      canonical
      twitterTitle
      twitterDescription
      twitterImage {
        localFile {
          childImageSharp {
            fixed(toFormat: JPG, width: 1024, height: 512) {
              src
            }
          }
        }
      }
    }
  }
`;

export default LayoutMain;
